import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectFilesService } from '../api/project-files.service';
import { PROJECT_FILE_KEYS } from './queryKeys';
import type { ProjectFileDto } from '../types';

interface UploadProjectFileInput {
  file: File;
  folderId?: string;
  /** Origin marker, e.g. `'whiteboard'`. */
  source?: string;
}

/**
 * Uploads a file and resolves the stored record.
 *
 * @remarks
 * The upload endpoint answers with the new id alone, but a caller that has to reference the
 * file afterwards — a whiteboard attachment shape, for instance — needs its storage key and
 * server-side metadata too. Fetching the record here keeps that second round trip in one place
 * instead of leaving every caller to chain it.
 */
export function useUploadProjectFile(projectId: string) {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useOrganizationStore();

  return useMutation({
    mutationFn: async ({ file, folderId, source }: UploadProjectFileInput) => {
      const { data: created } = await projectFilesService.uploadFile(
        projectId,
        file,
        folderId,
        source,
      );
      if (!created.id) throw new Error('Upload returned no file id');

      const { data } = await projectFilesService.getFile(projectId, created.id);
      return data satisfies ProjectFileDto;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_KEYS.byProject(activeOrganizationId ?? '', projectId),
      });
    },
  });
}
